import type { Concern } from '@/lib/calculator';
import { Flame, PawPrint, Bug, Droplets, Flower2, UtensilsCrossed, Wind, LucideIcon } from 'lucide-react';

// Room sizes for pSEO pages (increments of 50 for smaller, 100 for larger)
export const ROOM_SIZES = [100, 150, 200, 250, 300, 350, 400, 450, 500, 600, 700, 800, 900, 1000, 1200, 1500] as const;

export type RoomSize = typeof ROOM_SIZES[number];

// Room types with display names and slugs
export interface RoomType {
  name: string;
  slug: string;
  defaultCeilingHeight: number; // in feet
}

export const ROOM_TYPES: RoomType[] = [
  { name: 'Bedroom', slug: 'bedroom', defaultCeilingHeight: 8 },
  { name: 'Living Room', slug: 'living-room', defaultCeilingHeight: 9 },
  { name: 'Nursery', slug: 'nursery', defaultCeilingHeight: 8 },
  { name: 'Home Office', slug: 'home-office', defaultCeilingHeight: 8 },
  { name: 'Basement', slug: 'basement', defaultCeilingHeight: 7 },
  { name: 'Studio', slug: 'studio', defaultCeilingHeight: 9 },
  { name: 'Master Bedroom', slug: 'master-bedroom', defaultCeilingHeight: 9 },
  { name: 'Kitchen', slug: 'kitchen', defaultCeilingHeight: 8 },
];

// Air-quality problems. Each maps to the calculator's concerns; no per-problem ACH guesses.
export interface Problem {
  name: string;
  slug: string;
  concerns: Concern[];
  icon: LucideIcon;
  description: string;
  tips: string[];
}

export const PROBLEMS: Problem[] = [
  { name: 'Wildfire Smoke', slug: 'wildfire-smoke', concerns: ['wildfire'], icon: Flame,
    description: 'Wildfire smoke is mostly fine particles. Sizing uses smoke CADR and AHAM\'s floor-area rule (7.5 eACH). It is a filtration target, not a guarantee of safe exposure.',
    tips: ['Keep windows and doors closed while outdoor air is smoky, if it is safe to do so.', 'Set your HVAC to recirculate and consider a higher-MERV furnace filter if your system supports it.', 'Pick one room to keep cleanest and run the purifier there.', 'Follow local air-quality advisories (AirNow in the US).'] },
  { name: 'Pet Dander', slug: 'pet-dander', concerns: ['pets'], icon: PawPrint,
    description: 'Airborne dander is a particle problem, checked against dust CADR. Hair and settled dander on floors and furniture still need cleaning — a purifier only handles what is in the air.',
    tips: ['Vacuum with a sealed/HEPA vacuum and wash pet bedding.', 'Keep pets out of the bedroom if you react to them.', 'A pre-filter you can clean helps with hair.'] },
  { name: 'Dust Mites', slug: 'dust-mites', concerns: ['dust'], icon: Bug,
    description: 'Mite allergen is mostly in bedding and settles quickly, so a purifier only catches it while it is airborne. Bedding care does more.',
    tips: ['Wash bedding weekly in hot water.', 'Use allergen-impermeable mattress and pillow covers.', 'Keep indoor humidity below about 50%.'] },
  { name: 'Mold', slug: 'mold', concerns: ['general'], icon: Droplets,
    description: 'A HEPA purifier can capture airborne spores, but it does nothing about the moisture that lets mould grow. Fixing the damp is the actual solution.',
    tips: ['Find and fix leaks or condensation first.', 'Keep humidity below about 50–60%.', 'Large or recurring mould needs professional remediation.'] },
  { name: 'Allergies', slug: 'allergies', concerns: ['allergies'], icon: Flower2,
    description: 'Pollen is checked against pollen CADR. A purifier lowers airborne allergen levels; it is not a treatment, and results vary between people.',
    tips: ['Keep windows closed on high-pollen days.', 'Change clothes and shower after time outdoors.', 'Run the purifier in the bedroom overnight.'] },
  { name: 'Cooking Odors', slug: 'cooking-odors', concerns: ['general', 'gases'], icon: UtensilsCrossed,
    description: 'Cooking produces particles and gases. A purifier can help with the particles; for smells and gases, a range hood vented outdoors is the main control.',
    tips: ['Run the range hood before, during and after cooking.', 'Open a window when outdoor air is clean.', 'Carbon filters in standard purifiers are small and saturate; do not rely on them alone.'] },
  { name: 'General Air Quality', slug: 'general', concerns: ['general'], icon: Wind,
    description: 'Everyday particle filtration using this site\'s 5 eACH default.',
    tips: ['Place the unit where air can flow freely around it.', 'Replace filters on schedule.', 'Close the door of the room you want cleanest.'] },
];

// Parsed guide parameters from URL
export interface GuideParams {
  size: number;
  roomType: RoomType;
  problem: Problem;
}

/**
 * Generate a URL-friendly slug for a guide page
 */
export function generateGuideSlug(size: number, roomTypeSlug: string, problemSlug: string): string {
  return `${size}-sqft-${roomTypeSlug}-for-${problemSlug}`;
}

/**
 * Generate the full path for a guide
 */
export function generateGuidePath(size: number, roomTypeSlug: string, problemSlug: string): string {
  return `/guide/${generateGuideSlug(size, roomTypeSlug, problemSlug)}`;
}

/**
 * Parse a guide slug to extract parameters
 * Returns null if the slug is invalid
 */
export function parseGuideSlug(slug: string): GuideParams | null {
  // Expected format: {size}-sqft-{roomType}-for-{problem}
  const match = slug.match(/^(\d+)-sqft-(.+)-for-(.+)$/);
  
  if (!match) return null;
  
  const [, sizeStr, roomTypeSlug, problemSlug] = match;
  const size = parseInt(sizeStr, 10);
  
  // Validate size
  if (!ROOM_SIZES.includes(size as RoomSize)) return null;
  
  // Find room type
  const roomType = ROOM_TYPES.find(rt => rt.slug === roomTypeSlug);
  if (!roomType) return null;
  
  // Find problem
  const problem = PROBLEMS.find(p => p.slug === problemSlug);
  if (!problem) return null;
  
  return { size, roomType, problem };
}

/**
 * Check if a size is valid
 */
export function isValidSize(size: number): size is RoomSize {
  return ROOM_SIZES.includes(size as RoomSize);
}

/**
 * Get room type by slug
 */
export function getRoomTypeBySlug(slug: string): RoomType | undefined {
  return ROOM_TYPES.find(rt => rt.slug === slug);
}

/**
 * Get problem by slug
 */
export function getProblemBySlug(slug: string): Problem | undefined {
  return PROBLEMS.find(p => p.slug === slug);
}

// Priority rankings for generating top combinations
const SIZE_PRIORITY: number[] = [200, 300, 400, 500, 150, 250, 350, 600, 100, 450, 700, 800, 900, 1000, 1200, 1500];
const ROOM_TYPE_PRIORITY: string[] = ['bedroom', 'living-room', 'nursery', 'home-office', 'master-bedroom', 'basement', 'studio', 'kitchen'];
const PROBLEM_PRIORITY: string[] = ['allergies', 'pet-dander', 'wildfire-smoke', 'dust-mites', 'mold', 'general', 'cooking-odors'];

export interface GuideCombination {
  size: number;
  roomType: RoomType;
  problem: Problem;
  slug: string;
  path: string;
}

/**
 * Get top N guide combinations for the index page
 * Prioritizes common room sizes, popular room types, and frequent problems
 */
export function getTopCombinations(count: number = 50): GuideCombination[] {
  const combinations: GuideCombination[] = [];
  const seen = new Set<string>();
  
  // Round-robin: cycle through all problems so each gets representation
  // For each round, pick one combo per problem (cycling room types and sizes)
  const roomSizePairs: { roomSlug: string; size: number }[] = [];
  for (const size of SIZE_PRIORITY) {
    for (const roomSlug of ROOM_TYPE_PRIORITY) {
      roomSizePairs.push({ roomSlug, size });
    }
  }
  
  for (const pair of roomSizePairs) {
    for (const problemSlug of PROBLEM_PRIORITY) {
      if (combinations.length >= count) break;
      
      const roomType = getRoomTypeBySlug(pair.roomSlug);
      const problem = getProblemBySlug(problemSlug);
      
      if (roomType && problem) {
        const slug = generateGuideSlug(pair.size, pair.roomSlug, problemSlug);
        if (!seen.has(slug)) {
          seen.add(slug);
          combinations.push({
            size: pair.size,
            roomType,
            problem,
            slug,
            path: `/guide/${slug}`,
          });
        }
      }
    }
    if (combinations.length >= count) break;
  }
  
  return combinations.slice(0, count);
}

/**
 * Get combinations grouped by room type for the index page
 */
export function getCombinationsByRoomType(): Record<string, GuideCombination[]> {
  const all = getTopCombinations(100);
  const grouped: Record<string, GuideCombination[]> = {};
  
  for (const combo of all) {
    const key = combo.roomType.name;
    if (!grouped[key]) {
      grouped[key] = [];
    }
    grouped[key].push(combo);
  }
  
  return grouped;
}

/**
 * Get related guides for a given guide
 * Returns guides with same room type or same problem
 */
export function getRelatedGuides(params: GuideParams, count: number = 6): GuideCombination[] {
  const related: GuideCombination[] = [];
  const currentSlug = generateGuideSlug(params.size, params.roomType.slug, params.problem.slug);
  
  // First, add same room type with different problems
  for (const problem of PROBLEMS) {
    if (problem.slug !== params.problem.slug && related.length < count) {
      const slug = generateGuideSlug(params.size, params.roomType.slug, problem.slug);
      related.push({
        size: params.size,
        roomType: params.roomType,
        problem,
        slug,
        path: `/guide/${slug}`,
      });
    }
  }
  
  // Then, add same problem with different room types
  for (const roomType of ROOM_TYPES) {
    if (roomType.slug !== params.roomType.slug && related.length < count) {
      const slug = generateGuideSlug(params.size, roomType.slug, params.problem.slug);
      related.push({
        size: params.size,
        roomType,
        problem: params.problem,
        slug,
        path: `/guide/${slug}`,
      });
    }
  }
  
  // Finally, add nearby sizes
  const sizeIndex = ROOM_SIZES.indexOf(params.size as RoomSize);
  const nearbySizes = [
    ROOM_SIZES[sizeIndex - 1],
    ROOM_SIZES[sizeIndex + 1],
  ].filter(Boolean);
  
  for (const size of nearbySizes) {
    if (related.length < count && size) {
      const slug = generateGuideSlug(size, params.roomType.slug, params.problem.slug);
      related.push({
        size,
        roomType: params.roomType,
        problem: params.problem,
        slug,
        path: `/guide/${slug}`,
      });
    }
  }
  
  return related.filter(r => r.slug !== currentSlug).slice(0, count);
}

